import React, {ReactElement} from 'react';
import {Formik} from 'formik';
import {FormattedMessage, useIntl} from 'react-intl';
import {cx} from '@emotion/css';
import {CalculatorParams} from '@fo/shared-data/useBusinessLoanCalculator/useBusinessLoanCalculator';

import SinglePageForm from '@fo/designSystem/SinglePageForm/SinglePageForm';
import PercentageWithFormikField from '@fo/designSystem/Fields/Percentage/PercentageWithFormikField';
import CurrencyWithFormikField from '@fo/designSystem/Fields/Currency/CurrencyWithFormikField';
import RadioIconFieldWithFormikField from '@fo/designSystem/Fields/RadioIconField/RadioIconFieldWithFormikField';
import {ButtonInfo} from '@fo/designSystem/Fields/RadioIconField/RadioIconField';
import {MINIMUM_REQUEST_AMOUNT, MAXIMUM_REQUEST_AMOUNT, CURRENCY_SYMBOL} from '@fo/shared-types/constants';
import Button, {ButtonVariant} from '@fo/designSystem/Buttons/Button/Button';
import {segmentEvents, track} from '@fo/shared-analytics/tracking/segment';

import styles from './CalculatorForm.styles';

export interface CalculatorFormValues {
  interestRate?: string;
  loanAmount?: number;
  termLength: string;
}

interface Props {
  submitCalculator: ({interestRate, loanAmount, termLength}: CalculatorParams) => void;
  clearCalculator: () => void;
  initialValues?: CalculatorFormValues;
}

function CalculatorForm({submitCalculator, clearCalculator, initialValues = {termLength: '48'}}: Props): ReactElement {
  const {formatMessage} = useIntl();

  const generateTermLengthOptions = (): ButtonInfo[] => {
    const options: ButtonInfo[] = [];

    for (let index = 12; index <= 72; index += 12) {
      const indexString = index.toString();

      options.push({
        text: formatMessage(
          {id: 'phoenix.fields.businessLoanCalculator.termLength.option.label'},
          {value: indexString}
        ),
        value: indexString,
        key: `termsLengthOptions_${indexString}`,
      });
    }

    return options;
  };

  const convertValuesToNumbers = ({interestRate, loanAmount, termLength}: CalculatorFormValues): CalculatorParams => ({
    interestRate: interestRate ? parseFloat(interestRate) || 0 : 0,
    loanAmount: loanAmount || 0,
    termLength: parseInt(termLength, 10),
  });

  const isValueFilled = (value?: number): boolean => {
    if (typeof value !== 'undefined' && value.toString().length && value !== 0) {
      return true;
    }

    return false;
  };

  // value has to be string | number since different inputs return different types
  const handleCalculate = (values: CalculatorFormValues): void => {
    // It checks the length of the input value cause every field needs to be filled out for it to submit
    if (isValueFilled(values.interestRate?.length) && isValueFilled(values.loanAmount) && values.termLength.length) {
      const numberValues = convertValuesToNumbers(values);
      submitCalculator(numberValues);
    } else {
      clearCalculator();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {
        // no-op
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({errors, handleSubmit, setFieldValue, values}) => (
        <>
          <SinglePageForm
            errors={errors}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className={cx(styles.calculatorForm)}
          >
            <CurrencyWithFormikField<CalculatorFormValues>
              label={formatMessage({id: 'phoenix.fields.businessLoanCalculator.loanAmount.label'})}
              placeholder={formatMessage({id: 'phoenix.fields.businessLoanCalculator.loanAmount.placeholder'})}
              name="loanAmount"
              required
              minimumValue={MINIMUM_REQUEST_AMOUNT}
              maximumValue={MAXIMUM_REQUEST_AMOUNT}
              warningMessage={
                values.loanAmount &&
                values.loanAmount < MINIMUM_REQUEST_AMOUNT &&
                formatMessage(
                  {id: 'phoenix.errors.number.minWithCurrency'},
                  {currency: CURRENCY_SYMBOL, minimumValue: MINIMUM_REQUEST_AMOUNT}
                )
              }
            />
            <PercentageWithFormikField<CalculatorFormValues>
              label={formatMessage({id: 'phoenix.fields.businessLoanCalculator.interestRate.label'})}
              name="interestRate"
              placeholder={formatMessage({id: 'phoenix.fields.businessLoanCalculator.interestRate.placeholder'})}
              assistiveMessage={formatMessage({
                id: 'phoenix.fields.businessLoanCalculator.interestRate.assistiveMessage',
              })}
              required
              onChange={(name, value) => {
                setFieldValue(name, value);
              }}
              errorMessages={{required: formatMessage({id: 'phoenix.errors.numberOfShareholders.empty'})}}
            />
            <RadioIconFieldWithFormikField<CalculatorFormValues>
              label={formatMessage({id: 'phoenix.fields.businessLoanCalculator.termLength.label'})}
              name="termLength"
              required
              onChange={(name, value) => {
                setFieldValue(name, value);
              }}
              optionClassname={cx(styles.radioField)}
              options={generateTermLengthOptions()}
            />
          </SinglePageForm>
          <div className={cx(styles.calculateButtonContainer)}>
            <Button
              variant={ButtonVariant.PRIMARY}
              ariaLabel={formatMessage({id: 'businessLoanCalculator.buttonSubmitCalculation'})}
              onClick={() => {
                // Prevents the button to stay on "pressed" style after clicked
                (document.activeElement as HTMLButtonElement)?.blur();
                track(segmentEvents.pages.blc.calculateButtonClicked, {
                  calc_amount: values.loanAmount,
                  cal_rate: values.interestRate,
                  calc_how_long: values.termLength,
                });
                handleCalculate(values);
              }}
              testId="button.calculatorForm.calculate"
            >
              <FormattedMessage id="businessLoanCalculator.buttonSubmitCalculation" />
            </Button>
          </div>
        </>
      )}
    </Formik>
  );
}

export default CalculatorForm;
